import React, { useEffect } from 'react';

/**
 * GoneRoute — semantic 410 for retired or malformed routes.
 *
 * HussarAi is a client-side SPA behind Kubernetes ingress; we cannot return
 * a true HTTP 410 for these paths (nginx would need per-path rewrites we
 * don't control). This component gives us the best-available signal:
 *
 *   - Renders a minimal "This page has been retired" notice — dignified,
 *     no marketing, no redirect back into HussarAi doctrine copy.
 *   - Sets `<meta name="robots" content="noindex, nofollow">` so search
 *     engines deindex the URL on next crawl.
 *   - Sets a `<meta http-equiv="Status" content="410 Gone">` hint. Most
 *     crawlers ignore this in favour of the real HTTP status, but it's a
 *     zero-cost signal for the ones that do read it.
 *   - Sets the page title to reflect the retired state.
 *   - Does NOT redirect. The user's directive: "Do not redirect old
 *     LeadWing/HVAC/export/lead-operator routes into HussarAi immigration
 *     pages. They should be clearly retired."
 *
 * Variants (2026-02-19 addition):
 *   - default (unset)   → LeadWing-era retired route copy (existing behavior).
 *   - "malformed-login" → Malformed-URL SEO scrub for encoded-whitespace
 *                          prefixed /login paths (e.g. /%20login). Points
 *                          people politely at the real /login route.
 *
 * Old LeadWing routes handled here:
 *   /leadwing, /leadwing-pl, /hvac-wroclaw, /polski-operator-leadow-ai,
 *   /eksport, /export, /lead-magnet, /agenci-ai-dla-msp, /vs/bielik,
 *   /operator, /operator/leads, /pricing
 */
const GoneRoute = ({ variant }) => {
  const isMalformedLogin = variant === 'malformed-login';

  useEffect(() => {
    document.title = isMalformedLogin
      ? 'Nieprawidłowy adres URL · HussarAi'
      : 'Strona wycofana · HussarAi';
    const head = document.head;

    const upsertMeta = (name, content, useHttpEquiv = false) => {
      const attr = useHttpEquiv ? 'http-equiv' : 'name';
      let el = head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const robots = upsertMeta('robots', 'noindex, nofollow');
    const status = upsertMeta('Status', '410 Gone', true);

    return () => {
      // Restore robots to default when navigating away.
      robots.setAttribute('content', 'index, follow');
      status.setAttribute('content', '200 OK');
    };
  }, [isMalformedLogin]);

  const eyebrow = isMalformedLogin
    ? '410 · Nieprawidłowy adres'
    : '410 · Strona wycofana';
  const h1Pl = isMalformedLogin
    ? 'Ten adres jest nieprawidłowy.'
    : 'Ta strona została wycofana z HussarAi.';
  const bodyPl = isMalformedLogin
    ? 'Ten adres URL jest zniekształcony (zawiera zakodowaną spację przed „login") i nie odpowiada żadnej stronie w HussarAi. Prawidłowy adres logowania to /login.'
    : 'HussarAi jest europejską platformą spójności akt imigracyjnych. Treści związane z komercyjnym operatorem leadów zostały przeniesione poza HussarAi i nie są już dostępne pod tym adresem.';
  const bodyEn = isMalformedLogin
    ? 'This URL is malformed (it contains an encoded space before "login") and does not correspond to any HussarAi page. The correct login address is /login.'
    : 'This page has been retired from HussarAi. HussarAi is the European immigration-record integrity platform. Commercial lead-operator content has been moved outside HussarAi and is no longer served here.';
  const ctaHref = isMalformedLogin ? '/login' : '/';
  const ctaPl = isMalformedLogin
    ? 'Przejdź do logowania →'
    : 'HussarAi — strona główna →';

  return (
    <div
      data-testid="gone-route"
      data-variant={variant || 'default'}
      style={{
        minHeight: '100vh',
        background: '#0A0A0A',
        color: '#E2E8F0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 20px',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <p
        style={{
          fontSize: 11,
          letterSpacing: 1.6,
          fontWeight: 700,
          color: '#7DD3FC',
          textTransform: 'uppercase',
          marginBottom: 16,
        }}
      >
        {eyebrow}
      </p>
      <h1
        data-testid={isMalformedLogin ? 'gone-route-malformed-login-h1' : 'gone-route-h1'}
        style={{
          fontSize: 24,
          fontWeight: 700,
          margin: 0,
          maxWidth: 640,
          lineHeight: 1.3,
        }}
      >
        {h1Pl}
      </h1>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.6,
          marginTop: 16,
          maxWidth: 560,
          color: 'rgba(226,232,240,0.72)',
        }}
      >
        {bodyPl}
      </p>
      <p
        style={{
          fontSize: 13,
          lineHeight: 1.6,
          marginTop: 24,
          maxWidth: 560,
          color: 'rgba(226,232,240,0.55)',
        }}
      >
        {bodyEn}
      </p>
      <a
        href={ctaHref}
        data-testid="gone-route-home-link"
        style={{
          marginTop: 32,
          fontSize: 13,
          fontWeight: 600,
          color: '#7DD3FC',
          textDecoration: 'none',
          padding: '10px 18px',
          border: '1px solid rgba(125,211,252,0.35)',
          borderRadius: 8,
        }}
      >
        {ctaPl}
      </a>
    </div>
  );
};

export default GoneRoute;
